import React, {useState, useEffect} from "react"
import PropTypes from "prop-types"

import FormattedMessage, {InjectIntl, injectIntl, getMessage} from "@irati/utils/lang"
import {Body, H2} from "@irati/ui/src/Typography"
import mainTheme from "@irati/ui/src/global/theme"
import Button from "@irati/ui/src/Button"
import Flex from "@irati/ui/src/Flex"
import GenericModal from "@irati/ui/src/GenericModal"
import {IconTypes} from "@irati/ui/src/Icon"
import {RenewalQuote} from "@irati/types"
import FilterChip from "@irati/ui/src/FilterChip"
import intercom from "@irati/utils/intercom"
import salesService from "@irati/sales/services/sales"
import authentication from "@irati/auth"
import {getErrorMessage, getErrorValues} from "@irati/utils/lang/formatError"
import ErrorText from "@irati/ui/src/ErrorText"
import History from "@irati/utils/history"
import adminTracking, {RenewalPlanType, RenewalPlanSubSource, RenewalPlanPlan} from "@irati-tracking/admin/admin"

import RenewalCard from "../../RenewalCard/index"
import {AddOns} from "../../Renewal/Header"
import {BILLING_TABS} from "../../../containers/Billing"
import {CancellationInput} from "./index"

interface Props extends InjectIntl {
  show: boolean
  addOns: AddOns[]
  cancellation?: CancellationInput
  onHide: (ignoreTracking: boolean) => void
  onPrevious: () => void
  onNext: () => void
  onTimeout: () => void
}

const WinbackModal: React.FC<Props> = ({show, addOns, cancellation, onHide, onPrevious, onNext, onTimeout, formatMessage}, context) => {
  const [showModal, setShowModal] = useState<boolean>(show)
  const [renewalQuote, setRenewalQuote] = useState<RenewalQuote>()
  const [error, setError] = useState<string>()

  const openIntercom = () => {
    intercom.addCustomMessage("")
    onHide(true)
  }

  const handleCancellation = () => {
    adminTracking.renewalPlan({
      stage: 4,
      type: RenewalPlanType.cancellationFlow,
      reason: cancellation?.reason,
      action: "confirmCancellation"
    })
    setError("")
    salesService
      .cancelRenewalQuote({
        businessId: authentication.business().uuid,
        etag: renewalQuote?.etag,
        reason: cancellation?.reason,
        reasonText: cancellation?.reasonText
      })
      .then(onNext)
      .catch((err: PBError) => {
        const errorCode = err?.networkError?.errorData?.errorCode
        if (errorCode?.includes("SalesforceTimeout")) {
          onTimeout()
        } else {
          const message = getMessage(formatMessage, getErrorMessage(err), getErrorValues(err))
          setError(message)
        }
      })
  }

  const handleOfferSelect = () => {
    adminTracking.renewalPlan({
      stage: 4,
      subSource: RenewalPlanSubSource.winbackCTA,
      type: RenewalPlanType.cancellationFlow,
      reason: cancellation?.reason,
      plan: RenewalPlanPlan.Winback,
      action: "proceedWithWinback"
    })
    onHide(true)
    History.push({pathname: `/billing/${BILLING_TABS.NEW_PLAN}`, state: {winbackOption: renewalQuote?.winbackOption}}, context)
  }

  useEffect(() => {
    if (show && !renewalQuote) {
      salesService
        .getRenewalQuote(authentication.business().uuid)
        .then((quote: RenewalQuote) => {
          setRenewalQuote(quote)
        })
        .catch((err: PBError) => {
          const message = getMessage(formatMessage, getErrorMessage(err), getErrorValues(err))
          setError(message)
        })
    }
    setShowModal(show)
  }, [show])

  return (
    <GenericModal customWidth={800} hideActions show={showModal} onHide={onHide}>
      <H2 marginTop={2} marginBottom={renewalQuote?.winbackOption ? 1 : 3} color={mainTheme.colors.filterChip}>
        {renewalQuote?.winbackOption ? (
          <FormattedMessage id="admin.billing.currentPlan.winbackModal.header" />
        ) : (
          <FormattedMessage id="admin.billing.currentPlan.winbackModal.noPricing.body" />
        )}
      </H2>
      {renewalQuote?.winbackOption && (
        <>
          <Body marginBottom={2} color={mainTheme.colors.greys.medium}>
            <FormattedMessage id="admin.billing.currentPlan.winbackModal.body" />
          </Body>
          <Body small marginBottom={1} color={mainTheme.colors.greys.medium}>
            <FormattedMessage id="admin.billing.currentPlan.winbackModal.body2" />
          </Body>
        </>
      )}
      {renewalQuote?.winbackOption && (
        <>
          <Flex minWidth="700px" direction="column" marginBottom={5}>
            {addOns && (
              <Flex gap={mainTheme.spacing.one}>
                {addOns.map((data, i) => (
                  <FilterChip
                    key={i}
                    bgColor={mainTheme.colors.primary2}
                    textColor={mainTheme.colors.utility.white}
                    icon={data?.icon}
                    fillColor={data?.icon !== IconTypes.flexi ? mainTheme.colors.primary2 : null}>
                    {formatMessage({id: data.label})}
                  </FilterChip>
                ))}
              </Flex>
            )}
          </Flex>
          <Flex flex="1 1 33%" marginBottom={4}>
            <RenewalCard
              isSelected
              invertColors
              quote={renewalQuote?.winbackOption}
              oneYearQuote={renewalQuote?.options.find((q) => q.termLength === 12)}
              currencyCode={renewalQuote?.currency}
            />
          </Flex>
        </>
      )}
      {error && <ErrorText>{error}</ErrorText>}
      <Flex alignItems="center" justifyContent="space-between">
        <Button cta onClick={handleCancellation}>
          <FormattedMessage id="admin.billing.currentPlan.winbackModal.btn.cancellation" />
        </Button>
        <div>
          <Button marginRight onClick={onPrevious}>
            <FormattedMessage id="admin.billing.currentPlan.winbackModal.btn.previous" />
          </Button>
          {renewalQuote?.winbackOption ? (
            <Button primary onClick={handleOfferSelect}>
              <FormattedMessage id="admin.billing.currentPlan.winbackModal.btn.offer" />
            </Button>
          ) : (
            <Button primary onClick={openIntercom}>
              <FormattedMessage id="admin.billing.currentPlan.winbackModal.noPricing.btn" />
            </Button>
          )}
        </div>
      </Flex>
    </GenericModal>
  )
}

WinbackModal.contextTypes = {
  appPath: PropTypes.string
}

export default injectIntl(WinbackModal)
